import { skipToken, useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import type { ContentReferenceContext } from '@gitbook/app-database';
import type { NavigationScreenSpaceState } from '@gitbook/app-navigation';
import * as hiveCore from '@gitbook/hive-core';
import { hasPageCover } from '@gitbook/hive-core';
import {
    useHiveBranchSetPresence,
    useHiveComputedOrigin,
    useHiveRevision,
} from '@gitbook/hive-react';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Description,
    EmptyState,
    EmptyStateActions,
    EmptyStateIcon,
    EmptyStateNotFoundHeading,
    Heading,
} from '@gitbook/spine-react';
import { useStateWithDeps } from '@gitbook/util-react-hooks';

import {
    ContentReferenceContextProvider,
    ContentSourceSpaceBlankslate,
    NavigateToSpacePath,
    PageMetadata,
    useCurrentHivePage,
    usePageLayoutOptions,
} from '../../../Components';
import { NavigationRedirect, useNavigation, useSetAnalyticsScreen } from '../../../navigation';
import { apiQuery } from '../../../state';
import {
    PageContentSkeleton,
    PageExpressionContext,
    PageSheetView,
    type PageSheetViewProps,
    PageToolbar,
} from '../Page';
import { PageSkeleton } from '../Skeleton';
import { TableOfContents, TableOfContentsDesktopToggleButton } from '../TableOfContents';
import { SpaceHeadTitle } from './SpaceHeadTitle';

type SpaceRevisionContentProps = {
    space: api.Space;
    changeRequest: api.ChangeRequest | undefined;
    readOnly: boolean;
    withSidePanel: boolean;
    screen: NavigationScreenSpaceState;
    analyticsContext: analytics.AnalyticsContextSpaceContent;
    showToC?: boolean;
};

/**
 * Inner content of a space.
 */
export function SpaceRevisionContent(props: SpaceRevisionContentProps): React.ReactElement {
    const {
        space,
        changeRequest,
        readOnly,
        screen,
        analyticsContext: analyticsContextProp,
        withSidePanel,
    } = props;
    const { revision, sourceRevision, isComputed } = useHiveRevision();

    const [currentPage, redirectTo] = useCurrentHivePage(revision, screen.page);
    const currentPageId = currentPage?.id;

    const scrollRef = React.useRef<HTMLDivElement | null>(null);

    const pageLayoutOptions = usePageLayoutOptions(currentPage);

    // Table of contents state
    const [showToCToggleButton, setShowToCToggleButton] = React.useState(false);
    const [showToC, setShowToC] = useStateWithDeps(!!pageLayoutOptions.tableOfContents, [
        !!pageLayoutOptions.tableOfContents,
    ]);
    // With a full-width page cover the ToC toggle button needs to be `secondary` to ensure it's visible
    const pageHasFullWidthCover =
        currentPage &&
        hasPageCover(currentPage) &&
        !!pageLayoutOptions.cover &&
        pageLayoutOptions.coverSize === hiveCore.PageLayoutOptionsCoverSize.Full;

    const analyticsContext: analytics.AnalyticsContextSpaceContent = React.useMemo(() => {
        return {
            ...analyticsContextProp,
            page: currentPageId,
        };
    }, [analyticsContextProp, currentPageId]);

    const contentReferenceContext: ContentReferenceContext = React.useMemo(
        () => ({
            organization: space.organization,
            space: space.id,
            page: currentPageId,
            changeRequest: changeRequest?.id,
            revision: screen.type === 'space' && screen.revision ? screen.revision : undefined,
        }),
        [
            currentPageId,
            space.id,
            changeRequest?.id,
            screen.type,
            screen.revision,
            space.organization,
        ]
    );

    // If the screen state points to reusable content, but it doesn't exist in the revision, redirect
    // to the root space page. Once we have a reusable content modal, we might consider keeping
    // the state and showing "reusable content not found" in the modal.
    if (screen.reusableContent && !revision.reusableContents[screen.reusableContent]) {
        return (
            <NavigationRedirect
                to={{
                    current: {
                        reusableContent: undefined,
                    },
                }}
            />
        );
    }

    return (
        <ContentReferenceContextProvider value={contentReferenceContext}>
            <div className="flex min-h-0 flex-1">
                {showToC && (
                    <TableOfContents
                        space={space}
                        changeRequest={changeRequest}
                        currentPage={currentPage}
                        readOnly={readOnly}
                        withSidePanel={withSidePanel}
                        analyticsContext={analyticsContext}
                        setShowToCToggleButton={setShowToCToggleButton}
                    />
                )}

                <div className="relative flex flex-1">
                    <TableOfContentsDesktopToggleButton
                        isOpened={showToC}
                        setIsOpened={setShowToC}
                        showToCToggleButton={showToCToggleButton}
                        kind={pageHasFullWidthCover ? 'secondary' : 'blank'}
                    />

                    <React.Suspense
                        fallback={
                            <SpaceRevisionContentPageSkeleton page={currentPage} space={space} />
                        }
                    >
                        {(() => {
                            if (!currentPage) {
                                if (!isComputed) {
                                    return (
                                        <SpaceRevisionContentPageSkeleton
                                            page={null}
                                            space={space}
                                        />
                                    );
                                }

                                return (
                                    <SpaceRevisionContentNotFound
                                        space={space}
                                        pagePath={screen.page}
                                        revision={revision}
                                        sourceRevision={sourceRevision}
                                    />
                                );
                            }

                            return (
                                <>
                                    {redirectTo ? (
                                        // If the canonical path differs, update the URL in-place
                                        // but keep rendering the current content to avoid a flash.
                                        <CanonicalizePagePath redirectTo={redirectTo} />
                                    ) : null}
                                    <div
                                        ref={scrollRef}
                                        className="flex flex-1 flex-col overflow-y-auto"
                                    >
                                        <div className="flex min-h-min flex-1 flex-col">
                                            <SpaceHeadTitle
                                                title={currentPage.title}
                                                space={space}
                                            />
                                            <SpaceRevisionContentPage
                                                {...props}
                                                page={currentPage}
                                                revision={revision}
                                                scrollRef={scrollRef}
                                                withOutline={
                                                    Boolean(pageLayoutOptions?.outline) &&
                                                    !withSidePanel
                                                }
                                                showToC={showToC}
                                            />
                                        </div>
                                    </div>
                                </>
                            );
                        })()}
                    </React.Suspense>
                </div>
            </div>
        </ContentReferenceContextProvider>
    );
}

function SpaceRevisionContentPageSkeleton(props: {
    page: hiveCore.RevisionPageDocument | null;
    space: api.Space;
}) {
    const { page, space } = props;

    return (
        <>
            <SpaceHeadTitle title="Loading page" space={space} />
            {page ? <PageContentSkeleton page={page} /> : <PageSkeleton />}
        </>
    );
}

function SpaceRevisionContentPage(
    props: SpaceRevisionContentProps &
        Pick<PageSheetViewProps, 'scrollRef' | 'page' | 'revision' | 'withOutline'>
): React.ReactElement {
    const {
        space,
        scrollRef,
        revision,
        changeRequest,
        readOnly,
        screen,
        analyticsContext,
        page,
        showToC,
        withOutline,
    } = props;
    useSetAnalyticsScreen(
        page
            ? analytics.createPageEvent(
                  space,
                  page,
                  changeRequest
                      ? analytics.createChangeRequestEvent(space, changeRequest.id, {
                            screen: 'space_change_request',
                            changeRequestStatus: changeRequest.status,
                            diffMode: screen.mode === 'diff',
                            detailsPanel: screen.sideSheet?.type === 'overview',
                        })
                      : {
                            screen: 'space',
                            visibility: space.visibility,
                            diffMode: screen.mode === 'diff',
                        }
              )
            : null,
        [space.id, page?.id, changeRequest?.id, screen.mode]
    );
    useHiveBranchSetPresence({ presence: { pageId: page.id } });
    const computedOrigin = useHiveComputedOrigin({
        objectType: 'pages',
        objectId: page.id,
    });

    return (
        <PageExpressionContext page={page}>
            <PageSheetView
                key={getPageSheetReactKey(screen, revision, page)}
                screen={screen}
                space={space}
                changeRequest={changeRequest}
                revision={revision}
                page={page}
                computedOrigin={computedOrigin}
                readOnly={readOnly}
                withComments={space.permissions.comment && !screen.revision && !computedOrigin}
                withToC
                showToC={showToC}
                withDiff={screen.mode === 'diff'}
                analyticsContext={analyticsContext}
                scrollRef={scrollRef}
                withOutline={withOutline}
                footer={<PageMetadata page={page} className="border-t pt-6" />}
                toolbar={PageToolbar}
            />
        </PageExpressionContext>
    );
}

function SpaceRevisionContentNotFound(props: {
    space: api.Space;
    pagePath: string;
    revision: hiveCore.Revision;
    sourceRevision: hiveCore.Revision;
}): React.ReactElement {
    const { revision, space, pagePath, sourceRevision } = props;
    const hasDocument = React.useMemo(() => {
        const firstPageDocument = hiveCore.getFirstPageDocumentInRevision(revision);
        return !!firstPageDocument;
    }, [revision]);

    const computedPage = React.useMemo(() => {
        return Object.values(sourceRevision.pages).find(
            (page) => page.type === hiveCore.RevisionPageType.Computed
        );
    }, [sourceRevision]);

    if (hasDocument) {
        // If the space contains a page document, we can show the page not found state
        // as it means there is at least one page to display in the space
        return (
            <>
                <SpaceHeadTitle title="Page not found" space={space} />
                <SpaceRevisionContentPageNotFound
                    spaceId={space.id}
                    pagePath={pagePath}
                    revision={revision}
                />
            </>
        );
    }

    if (computedPage) {
        // If the space is empty (no page document), but it contains a computed page,
        // we show an optimized empty state for it
        return (
            <>
                <SpaceHeadTitle title="This space is empty" space={space} />
                <ContentSourceSpaceBlankslate space={space} source={computedPage.computed} />
            </>
        );
    }

    return (
        <>
            <SpaceHeadTitle title="This space is empty" space={space} />
            <EmptyState>
                <EmptyStateIcon>
                    <Icon.Page />
                </EmptyStateIcon>
                <Heading>This space is empty.</Heading>
                <Description>Get started by creating a first page.</Description>
            </EmptyState>
        </>
    );
}

function SpaceRevisionContentPageNotFound(props: {
    spaceId: string;
    pagePath: string;
    revision: hiveCore.Revision;
}): React.ReactElement {
    const { spaceId, pagePath, revision } = props;
    const { data } = useSuspenseQuery(
        apiQuery('getPageByPath', pagePath ? { spaceId, pagePath } : skipToken, {
            nullOnError: true,
        })
    );
    if (data && revision.pages[data.id]) {
        return (
            <NavigateToSpacePath to={hiveCore.getAbsolutePagePathInRevision(revision, data.id)} />
        );
    }
    return (
        <EmptyState>
            <EmptyStateIcon>
                <Icon.Page />
            </EmptyStateIcon>
            <EmptyStateNotFoundHeading />
            <Description>The page you were looking for could not be found.</Description>
            <EmptyStateActions>
                <Button
                    kind="secondary"
                    size="medium"
                    href={{
                        screen: {
                            type: 'space',
                            space: spaceId,
                            page: '',
                        },
                    }}
                    label="Browse space"
                />
            </EmptyStateActions>
        </EmptyState>
    );
}

/**
 * Side-effect component to canonicalize the current page path without unmounting the page content.
 *
 * Renaming a page title often updates its slug, so the canonical page path changes.
 * `useCurrentHivePage` exposes this via `redirectTo` (a canonical path different from the URL).
 *
 * This component imperatively replaces the URL to the canonical path using `{ replace: true }`.
 * It allows us to keep rendering the current page content while the URL changes, so the subtree
 * remains mounted.
 */
function CanonicalizePagePath({ redirectTo }: { redirectTo: string }) {
    const { navigate } = useNavigation();

    React.useEffect(() => {
        navigate(
            {
                screen: (prev) => {
                    if (prev.type !== 'space') {
                        return prev;
                    }

                    return {
                        ...prev,
                        page: redirectTo,
                    };
                },
            },
            { replace: true }
        );
    }, [navigate, redirectTo]);

    return null;
}

/**
 * Get the react key for the page sheet.
 * When the key is different, react will unmount / remount.
 * It simplifies the underlying lifecycle to ease the transitions between contexts (main to change request, etc).
 */
function getPageSheetReactKey(
    screen: NavigationScreenSpaceState,
    revision: hiveCore.Revision,
    page: hiveCore.RevisionPage
): string {
    let key = screen.space;

    key += `/${screen.changeRequest || 'main'}`;
    key += `/${revision.conflicts ? 'conflicts' : 'normal'}`;

    key += `/${page.id}`;

    if (screen.mode === 'diff') {
        key += '/diff';
    }

    if (screen.revision) {
        key += '/revision';
    }

    return key;
}
